/**
 * Meta Pixel wrapper — safe, typed helpers around window.fbq.
 * Silent no-op if the pixel script hasn't loaded (ad-blockers, dev, etc.)
 *
 * Standard events implemented:
 *   - PageView         (fired centrally by ScrollToTop.js on route change)
 *   - ViewContent      product-detail view
 *   - AddToCart        cart insertion
 *   - AddToWishlist    favorite toggled on
 *   - InitiateCheckout payment lightbox / checkout intent creation
 *   - AddPaymentInfo   (unused — Stripe iframe hides this event)
 *   - Purchase         thank-you page load
 *   - Lead             VIP list signup / support ticket
 *   - Schedule         stylist booking / appointment / homecoming event RSVP
 *   - CompleteRegistration signup success
 *   - Search           search-bar submission
 *
 * All amounts are in USD (currency: "USD").
 */

const _fbq = () => (typeof window !== "undefined" ? window.fbq : null);

/** Fire a Meta standard event. Silent no-op if fbq isn't loaded. */
const track = (event, params = {}) => {
  const fbq = _fbq();
  if (typeof fbq !== "function") return;
  try {
    fbq("track", event, params);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[meta-pixel]", event, e);
  }
};

const trackCustom = (event, params = {}) => {
  const fbq = _fbq();
  if (typeof fbq !== "function") return;
  try {
    fbq("trackCustom", event, params);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn("[meta-pixel]", event, e);
  }
};

const _num = (n) => (typeof n === "number" && !isNaN(n) ? n : Number(n) || 0);

/** ViewContent — user views a PDP. */
export const mpViewContent = (product, activeColor) => {
  if (!product) return;
  const content_id = activeColor ? `${product.id}:${activeColor}` : product.id;
  track("ViewContent", {
    content_ids: [content_id],
    content_type: "product",
    content_name: product.name,
    content_category: product.category_slug || product.category,
    value: _num(product.price),
    currency: "USD",
  });
};

/** AddToCart — user adds an item to their bag. */
export const mpAddToCart = ({ product, color, size, quantity = 1 }) => {
  if (!product) return;
  const content_id = color ? `${product.id}:${color}` : product.id;
  track("AddToCart", {
    content_ids: [content_id],
    content_type: "product",
    content_name: product.name,
    content_category: product.category_slug || product.category,
    contents: [{ id: content_id, quantity, item_price: _num(product.price) }],
    value: _num(product.price) * _num(quantity),
    currency: "USD",
    ...(size ? { content_variant: size } : {}),
  });
};

/**
 * InitiateCheckout — user starts a checkout flow (reserve / cart / credits).
 * kind: "reserve" | "cart" | "credits" | "bump" | "vip"
 */
export const mpInitiateCheckout = ({ items = [], value, num_items, kind }) => {
  const params = {
    content_type: kind === "credits" || kind === "vip" || kind === "bump" ? "product_group" : "product",
    value: _num(value),
    currency: "USD",
    num_items: _num(num_items || items.length || 1),
  };
  if (items.length) {
    params.content_ids = items.map((it) => it.id).filter(Boolean);
    params.contents = items.map((it) => ({
      id: it.id,
      quantity: _num(it.quantity || 1),
      item_price: _num(it.item_price || it.price || 0),
    }));
  }
  track("InitiateCheckout", params);
};

/** Purchase — payment succeeded on ThankYou page. */
export const mpPurchase = ({ orderId, value, items = [], kind }) => {
  const params = {
    value: _num(value),
    currency: "USD",
    num_items: _num(items.length || 1),
    content_type: "product",
  };
  if (orderId) params.order_id = orderId;
  if (items.length) {
    params.content_ids = items.map((it) => it.id).filter(Boolean);
    params.contents = items.map((it) => ({
      id: it.id,
      quantity: _num(it.quantity || 1),
      item_price: _num(it.item_price || it.price || 0),
    }));
  }
  if (kind) params.content_category = kind;
  track("Purchase", params);
};

/** Lead — VIP list signup, support ticket, general lead capture. */
export const mpLead = ({ source, value } = {}) => {
  const params = { currency: "USD" };
  if (typeof value === "number") params.value = value;
  if (source) params.content_name = source;
  track("Lead", params);
};

/** AddToWishlist — favorite/heart added to a product. */
export const mpAddToWishlist = (product, color) => {
  if (!product) return;
  const content_id = color ? `${product.id}:${color}` : product.id;
  track("AddToWishlist", {
    content_ids: [content_id],
    content_type: "product",
    content_name: product.name,
    content_category: product.category_slug || product.category,
    value: _num(product.price),
    currency: "USD",
  });
};

/** Schedule — appointment booking (stylist / try-on / homecoming RSVP). */
export const mpSchedule = ({ source } = {}) => {
  const params = { currency: "USD" };
  if (source) params.content_name = source;
  track("Schedule", params);
};

/** CompleteRegistration — new user signup success. */
export const mpCompleteRegistration = ({ method } = {}) => {
  track("CompleteRegistration", {
    status: "success",
    ...(method ? { content_name: method } : {}),
  });
};

/** Search — search bar submission. */
export const mpSearch = ({ query, category }) => {
  track("Search", {
    search_string: String(query || ""),
    ...(category ? { content_category: category } : {}),
  });
};

/** Custom event pass-through (e.g., TryOnGenerated). */
export const mpCustom = trackCustom;
